import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Move, Settings, 
  ChevronRight, Users, Clock, CheckCircle 
} from 'lucide-react';
import { workflowStagesService } from '../../services';
import './WorkflowStages.css';

const WorkflowStages = () => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    stage_order: 1,
    color: '#3B82F6'
  });

  useEffect(() => {
    loadStages();
  }, []);

  const loadStages = async () => {
    try {
      setLoading(true);
      const stagesData = await workflowStagesService.getStages();
      setStages(stagesData.sort((a, b) => a.stage_order - b.stage_order));
    } catch (error) {
      console.error('Failed to load workflow stages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStage = async (e) => {
    e.preventDefault();
    try {
      await workflowStagesService.createStage(formData);
      setShowCreateModal(false);
      setFormData({ name: '', description: '', stage_order: 1, color: '#3B82F6' });
      await loadStages();
    } catch (error) {
      console.error('Failed to create stage:', error);
      console.log('Error response:', error.response);
      console.log('Error response data:', error.response?.data);
      const errorMessage = error.response?.data?.message || 'Failed to create workflow stage. Please try again.';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleUpdateStage = async (e) => {
    e.preventDefault();
    try {
      await workflowStagesService.updateStage(selectedStage.id, formData);
      setShowEditModal(false);
      setSelectedStage(null);
      setFormData({ name: '', description: '', stage_order: 1, color: '#3B82F6' });
      await loadStages();
    } catch (error) {
      console.error('Failed to update stage:', error);
      alert('Failed to update workflow stage. Please try again.');
    }
  };

  const handleDeleteStage = async (stage) => {
    if (!window.confirm(`Are you sure you want to delete the "${stage.name}" stage?`)) {
      return;
    }

    try {
      await workflowStagesService.deleteStage(stage.id);
      await loadStages();
    } catch (error) {
      console.error('Failed to delete stage:', error);
      alert('Failed to delete workflow stage. This stage may have clients assigned to it.');
    }
  };

  const openEditModal = (stage) => {
    setSelectedStage(stage);
    setFormData({
      name: stage.name,
      description: stage.description || '',
      stage_order: stage.stage_order,
      color: stage.color
    });
    setShowEditModal(true);
  };

  const getNextStageOrder = () => {
    // Only consider active stages for calculating next order
    const activeStages = stages.filter(s => s.is_active);
    return activeStages.length > 0 ? Math.max(...activeStages.map(s => s.stage_order)) + 1 : 1;
  };

  if (loading) {
    return (
      <div className="workflow-stages-loading">
        <div className="loading-spinner"></div>
        <p>Loading workflow stages...</p>
      </div>
    );
  }

  return (
    <div className="workflow-stages">
      <div className="workflow-stages-header">
        <div>
          <h2>Workflow Stages</h2>
          <p>Customize your client management workflow</p>
        </div>
        <button 
          className="btn-primary"
          onClick={() => {
            setFormData({ 
              name: '', 
              description: '', 
              stage_order: getNextStageOrder(), 
              color: '#3B82F6' 
            });
            setShowCreateModal(true);
          }}
        >
          <Plus size={16} />
          Add Stage
        </button>
      </div>

      <div className="workflow-stages-pipeline">
        {stages.map((stage, index) => (
          <div key={stage.id} className="workflow-stage-card">
            <div 
              className="stage-header"
              style={{ borderTopColor: stage.color }}
            >
              <div className="stage-info">
                <div 
                  className="stage-color-indicator"
                  style={{ backgroundColor: stage.color }}
                ></div>
                <div>
                  <h3>{stage.name}</h3>
                  {stage.description && (
                    <p className="stage-description">{stage.description}</p>
                  )}
                </div>
              </div>
              <div className="stage-actions">
                <button 
                  className="btn-icon"
                  onClick={() => openEditModal(stage)}
                  title="Edit stage"
                >
                  <Edit2 size={16} />
                </button>
                <button 
                  className="btn-icon btn-danger"
                  onClick={() => handleDeleteStage(stage)}
                  title="Delete stage"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            
            <div className="stage-stats">
              <div className="stat">
                <Users size={14} />
                <span>0 clients</span>
              </div>
              <div className="stat">
                <Clock size={14} />
                <span>Order: {stage.stage_order}</span>
              </div>
            </div>

            {index < stages.length - 1 && (
              <div className="stage-arrow">
                <ChevronRight size={20} />
              </div>
            )}
          </div>
        ))}

        {stages.length === 0 && (
          <div className="empty-stages">
            <Settings size={48} />
            <h3>No workflow stages configured</h3>
            <p>Create your first workflow stage to start organizing your clients</p>
            <button 
              className="btn-primary"
              onClick={() => {
                setFormData({ 
                  name: '', 
                  description: '', 
                  stage_order: 1, 
                  color: '#3B82F6' 
                });
                setShowCreateModal(true);
              }}
            >
              <Plus size={16} />
              Create First Stage
            </button>
          </div>
        )}
      </div>

      {/* Create Stage Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Create Workflow Stage</h3>
              <button 
                className="btn-close"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateStage}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Stage Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Lead, Quote Sent, Booked"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe what happens in this stage"
                    rows="3"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Stage Order *</label>
                    <input
                      type="number"
                      value={formData.stage_order}
                      onChange={(e) => setFormData({ ...formData, stage_order: parseInt(e.target.value) })}
                      min="1"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Color</label>
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Stage
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Stage Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Workflow Stage</h3>
              <button 
                className="btn-close"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleUpdateStage}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Stage Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Lead, Quote Sent, Booked"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe what happens in this stage"
                    rows="3"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Stage Order *</label>
                    <input
                      type="number"
                      value={formData.stage_order}
                      onChange={(e) => setFormData({ ...formData, stage_order: parseInt(e.target.value) })}
                      min="1"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Color</label>
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Update Stage
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowStages;
